import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'

/**
 * Feature gates for the flash-related booth work, both registered in the Studio flag
 * registry (config/featureFlags.ts, surface "app") and both default OFF.
 *
 * They are deliberately SEPARATE switches: one changes capture timing on the paid
 * path, the other only renders operator guidance. A booth should be able to take the
 * harmless UI without opting into the behavioral change, and either can be killed
 * independently if a fleet misbehaves.
 *
 * Unknown keys resolve to `false` (featureFlagsStore.getFlag fallback), so a booth on
 * an older Studio — or offline before its first flag fetch — behaves exactly as it did
 * before this work landed.
 */

/**
 * Hardened shutter retry handling for flash booths: escalating busy backoff
 * (0.5s→1s→2s→4s instead of 2×600ms) plus a PTP event re-drain before each busy
 * re-press. OFF ⇒ the pre-existing flat schedule with no re-drain.
 *
 * Gated because it changes timing on the PAID capture path: a capture that ends up
 * failing anyway now takes up to ~7.5s longer before it surfaces, which a guest sees
 * as a longer wait. The happy path is untouched either way.
 */
export const FLASH_CAPTURE_RETRY_FLAG = 'flash_capture_retry'

/**
 * The camera-menu setup guide shown in Camera Settings when the operator has declared
 * flash use. OFF ⇒ the guide never renders. Display-only; no capture behavior changes.
 */
export const FLASH_SETUP_GUIDE_FLAG = 'flash_setup_guide'

/** Non-React read for the capture path (uniCamUtils runs outside the React tree). */
export function isFlashCaptureRetryEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(FLASH_CAPTURE_RETRY_FLAG, false)
}
